import { lazy, Suspense, useCallback } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  useIsHomeDisplayReady,
  useIsActivityQualificationAutoEnabled,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import { useUserDataFlattened } from "@/modules/user/stores";
import { ROUTES } from "@/types/routes";
import { firstRechargeStagedApi } from "@/api/firstRechargeStagedApi";

const FirstRechargeStagedAutoGrantDialog = lazy(() =>
  import("../components/FirstRechargeStaged/FirstRechargeStagedAutoGrantDialog").then(
    m => ({ default: m.FirstRechargeStagedAutoGrantDialog })
  )
);

const AUTO_CFG = POPUP_CONFIGS.FIRST_RECHARGE_STAGED_AUTO_GRANT;

export const useFirstRechargeStagedAutoGrantPopup = () => {
  const { isAuthenticated, data: userInfo, loadUserInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const pending = useHomeDisplayStore(
    s => s.data?.firstRechargeStaged?.autoGrantNotice?.pending === true
  );

  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.FIRST_RECHARGE_STAGED
  );

  const shouldShowFn = useCallback(() => {
    if (!isConfigReady || !isAuthenticated) return false;
    return pending;
  }, [isConfigReady, isAuthenticated, pending]);

  useQueuedPopup({
    popupId: AUTO_CFG.id,
    priority: AUTO_CFG.priority,
    shouldShow: shouldShowFn,
    renderContent: onComplete => (
      <Suspense fallback={null}>
        <FirstRechargeStagedAutoGrantDialog
          visible
          onConfirm={async () => {
            try {
              await firstRechargeStagedApi.autoAfterEndAck({});
              await useHomeDisplayStore.getState().refreshConfig();
              try {
                await loadUserInfo();
              } catch {
                /* ignore */
              }
            } catch (e) {
              console.error("[FirstRechargeStaged] auto_after_end_ack failed", e);
            } finally {
              onComplete();
            }
          }}
        />
      </Suspense>
    ),
    showOnce: false,
    allowManualTrigger: false,
    autoTrigger: isAutoEnabled,
    dependencies: [isConfigReady, isAuthenticated, pending, isAutoEnabled],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    blockedPaths: [
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId as string | undefined,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });
};
